/**
 * Framer Motion design tokens for the Vharta Design System.
 *
 * All animation durations, easing curves, and reusable variants are defined here.
 * Import these in components instead of defining ad-hoc animations.
 *
 * Usage:
 *   import { transition, variants } from '@/lib/motion'
 *
 *   <motion.div
 *     variants={variants.fadeInUp}
 *     initial="initial"
 *     animate="animate"
 *     transition={transition.normal}
 *   />
 */

import type { Variants, Transition } from 'framer-motion'

/* ===== Transitions ===== */

/** Quick micro-interaction (button press, toggle) */
export const transitionFast: Transition = {
  duration: 0.15,
  ease: [0.22, 1, 0.36, 1],
}

/** Standard transition (card hover, tab switch) */
export const transitionNormal: Transition = {
  duration: 0.25,
  ease: [0.22, 1, 0.36, 1],
}

/** Emphasized transition (modal enter, page slide) */
export const transitionSlow: Transition = {
  duration: 0.4,
  ease: [0.22, 1, 0.36, 1],
}

/** Spring for interactive/draggable elements */
export const transitionSpring: Transition = {
  type: 'spring',
  stiffness: 300,
  damping: 24,
}

/** Gentle spring for page-level transitions */
export const transitionGentle: Transition = {
  type: 'spring',
  stiffness: 150,
  damping: 20,
}

/** Named export object for convenience */
export const transition = {
  fast: transitionFast,
  normal: transitionNormal,
  slow: transitionSlow,
  spring: transitionSpring,
  gentle: transitionGentle,
} as const

/* ===== Variants ===== */

export const fadeIn: Variants = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
}

export const fadeInUp: Variants = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -8 },
}

export const fadeInDown: Variants = {
  initial: { opacity: 0, y: -16 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: 8 },
}

export const scaleIn: Variants = {
  initial: { opacity: 0, scale: 0.95 },
  animate: { opacity: 1, scale: 1 },
  exit: { opacity: 0, scale: 0.95 },
}

export const slideInRight: Variants = {
  initial: { x: '100%', opacity: 0 },
  animate: { x: 0, opacity: 1 },
  exit: { x: '100%', opacity: 0 },
}

export const slideInLeft: Variants = {
  initial: { x: '-100%', opacity: 0 },
  animate: { x: 0, opacity: 1 },
  exit: { x: '-100%', opacity: 0 },
}

/** Use on parent container; children with variants will stagger */
export const stagger: Variants = {
  initial: {},
  animate: {
    transition: {
      staggerChildren: 0.06,
    },
  },
}

/** Named export object for convenience */
export const variants = {
  fadeIn,
  fadeInUp,
  fadeInDown,
  scaleIn,
  slideInRight,
  slideInLeft,
  stagger,
} as const
